import React, { useEffect, useRef, useState } from 'react';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { useAuth } from '../context/AuthContext';
import { profileService } from '../services/profileService';
import { readImageAsDataUrl } from '../utils/imageFile';
import { getAssetUrl } from '../utils/assets';
import { browserTimeZone } from '../utils/datetime';
import TimezoneSelect from '../components/TimezoneSelect';
import ProfileUsageStats from '../components/ProfileUsageStats';
import {
  getBrowserNotificationPermission,
  requestBrowserNotifications
} from '../utils/browserNotifications';
import './ProfileSettings.css';

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super administrator',
  CLIENT_ADMIN: 'Client administrator',
  AGENT: 'Agent',
  END_USER: 'End user'
};

const ProfileSettings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [avatarPreview, setAvatarPreview] = useState<string | undefined>(
    getAssetUrl(user?.avatar)
  );
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState('');
  const [avatarError, setAvatarError] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [twoFactorMessage, setTwoFactorMessage] = useState('');
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [disableTwoFactorCode, setDisableTwoFactorCode] = useState('');
  const [twoFactorSetup, setTwoFactorSetup] = useState<{ qrCodeDataUrl: string; manualEntryKey: string } | null>(null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(user?.notifyEmail !== false);
  const [notifyBrowser, setNotifyBrowser] = useState(user?.notifyBrowser !== false);
  const [notifySound, setNotifySound] = useState(!!user?.notifySound);
  const [notifySaving, setNotifySaving] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyError, setNotifyError] = useState('');
  const [browserPermission, setBrowserPermission] = useState(getBrowserNotificationPermission);
  const [usernameValue, setUsernameValue] = useState(user?.username || '');
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameMessage, setUsernameMessage] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const usernameLocked = Boolean(user?.username);
  const [timezoneValue, setTimezoneValue] = useState(user?.timezone || browserTimeZone());
  const [timezoneSaving, setTimezoneSaving] = useState(false);
  const [timezoneMessage, setTimezoneMessage] = useState('');
  const [timezoneError, setTimezoneError] = useState('');

  const handleAvatarSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setAvatarMessage('');
    setAvatarError('');

    try {
      const dataUrl = await readImageAsDataUrl(file);
      setAvatarUploading(true);
      const avatarPath = await profileService.uploadAvatar(dataUrl);
      const avatarUrl = getAssetUrl(avatarPath);
      setAvatarPreview(avatarUrl);
      updateUser({ avatar: avatarPath });
      setAvatarMessage('Avatar saved successfully.');
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error uploading avatar';
      setAvatarError(message);
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarMessage('');
    setAvatarError('');

    try {
      setAvatarUploading(true);
      await profileService.removeAvatar();
      setAvatarPreview(undefined);
      updateUser({ avatar: undefined });
      setAvatarMessage('Avatar removed.');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error removing avatar';
      setAvatarError(message);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordMessage('');
    setPasswordError('');

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    try {
      setPasswordSaving(true);
      await profileService.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage('Password changed successfully.');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error changing password';
      setPasswordError(message);
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSetupTwoFactor = async () => {
    setTwoFactorMessage('');
    setTwoFactorError('');

    try {
      setTwoFactorLoading(true);
      const data = await profileService.setupTwoFactor();
      setTwoFactorSetup(data);
      setTwoFactorMessage('Scan the QR code and enter the app code to enable 2FA.');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error preparing 2FA';
      setTwoFactorError(message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleEnableTwoFactor = async () => {
    setTwoFactorMessage('');
    setTwoFactorError('');

    try {
      setTwoFactorLoading(true);
      const data = await profileService.enableTwoFactor(twoFactorCode);
      updateUser({
        twoFactorEnabled: true,
        twoFactorRecoveryCodesRemaining: data.twoFactorRecoveryCodesRemaining
      });
      setRecoveryCodes(data.recoveryCodes || []);
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      setTwoFactorMessage('2FA has been enabled. Save the recovery codes now; they are shown only once.');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error enabling 2FA';
      setTwoFactorError(message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisableTwoFactor = async () => {
    setTwoFactorMessage('');
    setTwoFactorError('');

    try {
      setTwoFactorLoading(true);
      await profileService.disableTwoFactor(disableTwoFactorCode);
      updateUser({ twoFactorEnabled: false, twoFactorRecoveryCodesRemaining: 0 });
      setDisableTwoFactorCode('');
      setTwoFactorSetup(null);
      setRecoveryCodes([]);
      setTwoFactorMessage('2FA has been disabled.');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error disabling 2FA';
      setTwoFactorError(message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleRegenerateRecoveryCodes = async () => {
    setTwoFactorMessage('');
    setTwoFactorError('');

    try {
      setTwoFactorLoading(true);
      const data = await profileService.regenerateRecoveryCodes(regenerateCode);
      setRecoveryCodes(data.recoveryCodes || []);
      setRegenerateCode('');
      updateUser({ twoFactorRecoveryCodesRemaining: data.twoFactorRecoveryCodesRemaining });
      setTwoFactorMessage('New recovery codes have been generated. The old ones are no longer valid.');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error generating recovery codes';
      setTwoFactorError(message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const copyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setTwoFactorMessage('Recovery codes copied.');
    } catch {
      setTwoFactorError('Copy failed.');
    }
  };

  const handleNotificationSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setNotifyMessage('');
    setNotifyError('');

    try {
      setNotifySaving(true);
      if (notifyBrowser) {
        const permission = await requestBrowserNotifications();
        setBrowserPermission(permission);
        if (permission !== 'granted') {
          setNotifyError(
            permission === 'unsupported'
              ? 'This browser does not support desktop popups.'
              : 'Allow notifications in the browser to use popups, then save again.'
          );
          setNotifySaving(false);
          return;
        }
      }

      const saved = await profileService.updateNotificationSettings({
        notifyEmail,
        notifyBrowser,
        notifySound
      });
      updateUser(saved);
      setNotifyMessage('Notification settings saved.');
    } catch (error: any) {
      setNotifyError(error.response?.data?.message || 'Error saving settings');
    } finally {
      setNotifySaving(false);
    }
  };

  const handleUsernameSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setUsernameMessage('');
    setUsernameError('');

    if (usernameLocked) {
      setUsernameError('Username is already set. Only a supervisor can change it.');
      return;
    }

    const normalizedUsername = usernameValue.trim().toLowerCase();
    if (!/^[a-z0-9._-]{3,30}$/.test(normalizedUsername)) {
      setUsernameError('Username must be 3-30 characters and can contain only letters, numbers, dots, hyphens, or underscores.');
      return;
    }

    try {
      setUsernameSaving(true);
      const savedUsername = await profileService.setUsername(normalizedUsername);
      setUsernameValue(savedUsername);
      updateUser({ username: savedUsername });
      setUsernameMessage('Username set successfully.');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error setting username';
      setUsernameError(message);
    } finally {
      setUsernameSaving(false);
    }
  };

  useEffect(() => {
    if (user?.timezone) {
      setTimezoneValue(user.timezone);
    }
  }, [user?.timezone]);

  const handleTimezoneSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setTimezoneMessage('');
    setTimezoneError('');

    try {
      setTimezoneSaving(true);
      const saved = await profileService.updateTimezone({
        timezone: timezoneValue,
        timezoneAuto: false
      });
      setTimezoneValue(saved.timezone);
      updateUser(saved);
      setTimezoneMessage('Timezone saved.');
    } catch (error: any) {
      setTimezoneError(error.response?.data?.message || 'Error saving timezone');
    } finally {
      setTimezoneSaving(false);
    }
  };

  const companyName = typeof user?.company === 'object' ? user?.company?.name : '';
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Your profile';

  return (
    <div className="profile-settings">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <i className="pi pi-user"></i>
          </div>
          <div>
            <h1 className="page-title">My Profile</h1>
            <p className="page-subtitle">Account, ticket stats, timezone, security and notifications</p>
          </div>
        </div>
      </div>

      <section className="profile-hero">
        <div className="profile-hero-avatar">
          {avatarPreview ? (
            <img src={avatarPreview} alt="Avatar" className="profile-avatar-image" />
          ) : (
            <Avatar
              label={user?.firstName?.charAt(0)}
              shape="circle"
              size="xlarge"
              className="profile-avatar-fallback"
            />
          )}
        </div>
        <div className="profile-hero-copy">
          <h2 className="profile-hero-name">{displayName}</h2>
          <div className="profile-hero-meta">
            {user?.email && (
              <span><i className="pi pi-envelope"></i> {user.email}</span>
            )}
            {companyName && (
              <span><i className="pi pi-building"></i> {companyName}</span>
            )}
            {user?.role && (
              <span><i className="pi pi-id-card"></i> {ROLE_LABELS[user.role] || user.role}</span>
            )}
          </div>
        </div>
        <div className="profile-hero-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleAvatarSelect}
            hidden
          />
          <Button
            type="button"
            label={avatarUploading ? 'Saving...' : 'Change photo'}
            icon="pi pi-image"
            className="submit-btn"
            disabled={avatarUploading}
            onClick={() => fileInputRef.current?.click()}
          />
          {avatarPreview && (
            <Button
              type="button"
              label="Remove"
              icon="pi pi-times"
              className="cancel-btn p-button-secondary"
              disabled={avatarUploading}
              onClick={handleRemoveAvatar}
            />
          )}
        </div>
      </section>
      {(avatarMessage || avatarError) && (
        <p className={avatarError ? 'profile-error' : 'profile-success'} style={{ margin: '-0.75rem 0 1rem' }}>
          {avatarError || avatarMessage}
        </p>
      )}

      <ProfileUsageStats />

      <div className="profile-stack">
        <div className="profile-row">
          <section className="profile-panel">
            <div className="profile-panel-head">
              <div className="profile-panel-icon"><i className="pi pi-at"></i></div>
              <div>
                <h2>Username</h2>
                <p className="profile-hint">
                  {usernameLocked
                    ? 'You can set this only once. A supervisor can change it later.'
                    : 'Set it once. After that only a supervisor can change it.'}
                </p>
              </div>
            </div>
            <form onSubmit={handleUsernameSubmit} className="profile-form">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <InputText
                  id="username"
                  className="field-input"
                  value={usernameValue}
                  onChange={(e) => setUsernameValue(e.target.value.toLowerCase())}
                  placeholder="e.g. jane.doe"
                  disabled={usernameLocked || usernameSaving}
                />
              </div>
              {usernameMessage && <div className="profile-success">{usernameMessage}</div>}
              {usernameError && <div className="profile-error">{usernameError}</div>}
              {!usernameLocked && (
                <div className="profile-form-actions">
                  <Button
                    type="submit"
                    label={usernameSaving ? 'Saving...' : 'Save username'}
                    icon="pi pi-check"
                    className="submit-btn"
                    disabled={usernameSaving || !usernameValue.trim()}
                  />
                </div>
              )}
            </form>
          </section>

          <section className="profile-panel">
            <div className="profile-panel-head">
              <div className="profile-panel-icon"><i className="pi pi-globe"></i></div>
              <div>
                <h2>Timezone</h2>
                <p className="profile-hint">Times on tickets, chat and the audit log follow this zone.</p>
              </div>
            </div>
            <form onSubmit={handleTimezoneSave} className="profile-form">
              <div className="field">
                <label htmlFor="profile-timezone">Your timezone</label>
                <TimezoneSelect
                  id="profile-timezone"
                  compact
                  value={timezoneValue}
                  onChange={setTimezoneValue}
                />
              </div>
              {timezoneMessage && <div className="profile-success">{timezoneMessage}</div>}
              {timezoneError && <div className="profile-error">{timezoneError}</div>}
              <div className="profile-form-actions">
                <Button
                  type="button"
                  label="Use this computer"
                  icon="pi pi-desktop"
                  className="p-button-secondary"
                  onClick={() => setTimezoneValue(browserTimeZone())}
                />
                <Button
                  type="submit"
                  label={timezoneSaving ? 'Saving...' : 'Save timezone'}
                  icon="pi pi-check"
                  className="submit-btn"
                  disabled={timezoneSaving}
                />
              </div>
            </form>
          </section>
        </div>

        <section className="profile-panel">
          <div className="profile-panel-head">
            <div className="profile-panel-icon"><i className="pi pi-lock"></i></div>
            <div>
              <h2>Password</h2>
              <p className="profile-hint">Enter your current password, then a new one (at least 8 characters).</p>
            </div>
          </div>
          <form onSubmit={handlePasswordSubmit} className="profile-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Current password</label>
              <InputText
                id="currentPassword"
                type="password"
                className="field-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New password</label>
              <InputText
                id="newPassword"
                type="password"
                className="field-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm new password</label>
              <InputText
                id="confirmPassword"
                type="password"
                className="field-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
            {passwordMessage && <div className="profile-success">{passwordMessage}</div>}
            {passwordError && <div className="profile-error">{passwordError}</div>}
            <div className="profile-form-actions">
              <Button
                type="submit"
                label={passwordSaving ? 'Saving...' : 'Change password'}
                icon="pi pi-check"
                className="submit-btn"
                loading={passwordSaving}
                disabled={passwordSaving}
              />
            </div>
          </form>
        </section>

        <section className="profile-panel">
          <div className="profile-panel-head">
            <div className="profile-panel-icon"><i className="pi pi-shield"></i></div>
            <div>
              <h2>Two-factor authentication</h2>
              <p className="profile-hint">Use Google Authenticator, Authy, or another TOTP app.</p>
            </div>
          </div>

          {!user?.twoFactorEnabled && !twoFactorSetup && (
            <div className="two-factor-panel">
              <p className="profile-hint">2FA is not enabled on this account.</p>
              <div className="profile-form-actions">
                <Button
                  type="button"
                  label={twoFactorLoading ? 'Preparing...' : 'Enable 2FA'}
                  icon="pi pi-shield"
                  className="submit-btn"
                  onClick={handleSetupTwoFactor}
                  disabled={twoFactorLoading}
                />
              </div>
            </div>
          )}

          {!user?.twoFactorEnabled && twoFactorSetup && (
            <div className="two-factor-panel">
              <img src={twoFactorSetup.qrCodeDataUrl} alt="2FA QR code" className="two-factor-qr" />
              <div className="two-factor-secret">
                <strong>Manual key:</strong> <code>{twoFactorSetup.manualEntryKey}</code>
              </div>
              <div className="form-group">
                <label htmlFor="twoFactorCode">Verification code</label>
                <InputText
                  id="twoFactorCode"
                  className="field-input"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                />
              </div>
              <div className="profile-form-actions">
                <Button
                  type="button"
                  label="Cancel"
                  icon="pi pi-times"
                  className="cancel-btn p-button-secondary"
                  onClick={() => {
                    setTwoFactorSetup(null);
                    setTwoFactorCode('');
                    setTwoFactorError('');
                    setTwoFactorMessage('');
                  }}
                />
                <Button
                  type="button"
                  label="Confirm 2FA"
                  icon="pi pi-check"
                  className="submit-btn"
                  onClick={handleEnableTwoFactor}
                  disabled={twoFactorLoading || twoFactorCode.length !== 6}
                />
              </div>
            </div>
          )}

          {user?.twoFactorEnabled && (
            <div className="two-factor-panel">
              <div className="two-factor-status">
                <i className="pi pi-shield"></i>
                <span>2FA is enabled · {user.twoFactorRecoveryCodesRemaining || 0} recovery codes left</span>
              </div>
              <div className="form-group">
                <label htmlFor="disableTwoFactorCode">Code to disable 2FA</label>
                <InputText
                  id="disableTwoFactorCode"
                  className="field-input"
                  value={disableTwoFactorCode}
                  onChange={(e) => setDisableTwoFactorCode(e.target.value.slice(0, 9))}
                  placeholder="123456 or ABCD-EFGH"
                />
              </div>
              <div className="profile-form-actions">
                <Button
                  type="button"
                  label="Disable 2FA"
                  icon="pi pi-times"
                  className="p-button-danger"
                  onClick={handleDisableTwoFactor}
                  disabled={twoFactorLoading || disableTwoFactorCode.trim().length < 6}
                />
              </div>
              <div className="form-group">
                <label htmlFor="regenerateCode">New recovery codes (enter 2FA code)</label>
                <InputText
                  id="regenerateCode"
                  className="field-input"
                  value={regenerateCode}
                  onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                />
              </div>
              <div className="profile-form-actions">
                <Button
                  type="button"
                  label="Regenerate recovery codes"
                  icon="pi pi-key"
                  className="submit-btn"
                  onClick={handleRegenerateRecoveryCodes}
                  disabled={twoFactorLoading || regenerateCode.length !== 6}
                />
              </div>
            </div>
          )}

          {twoFactorMessage && <div className="profile-success">{twoFactorMessage}</div>}
          {twoFactorError && <div className="profile-error">{twoFactorError}</div>}

          {recoveryCodes.length > 0 && (
            <div className="recovery-codes">
              <h3>Recovery codes</h3>
              <p className="profile-hint">Save these now. Each can be used only once if you lose your phone.</p>
              <ul>
                {recoveryCodes.map((code) => (
                  <li key={code}><code>{code}</code></li>
                ))}
              </ul>
              <Button
                type="button"
                label="Copy codes"
                icon="pi pi-copy"
                className="submit-btn"
                onClick={copyRecoveryCodes}
              />
            </div>
          )}
        </section>

        <section className="profile-panel">
          <div className="profile-panel-head">
            <div className="profile-panel-icon"><i className="pi pi-bell"></i></div>
            <div>
              <h2>Notifications</h2>
              <p className="profile-hint">Choose how you want to be notified.</p>
            </div>
          </div>
          <form onSubmit={handleNotificationSave} className="profile-form">
            <div className="notify-list">
              <label className="notify-toggle">
                <span className="notify-toggle-copy">
                  <span className="notify-toggle-title">Email notifications</span>
                  <span className="notify-toggle-hint">Ticket updates sent to your inbox</span>
                </span>
                <InputSwitch
                  checked={notifyEmail}
                  onChange={(e) => setNotifyEmail(!!e.value)}
                />
              </label>
              <label className="notify-toggle">
                <span className="notify-toggle-copy">
                  <span className="notify-toggle-title">Browser popup</span>
                  <span className="notify-toggle-hint">
                    {browserPermission === 'unsupported'
                      ? 'This browser does not support desktop popups.'
                      : browserPermission === 'denied'
                        ? 'Blocked in browser settings. Allow notifications for this site, then try again.'
                        : browserPermission === 'granted'
                          ? 'Desktop alerts while the app is open'
                          : 'The browser will ask for permission when you turn this on.'}
                  </span>
                </span>
                <InputSwitch
                  checked={notifyBrowser}
                  disabled={browserPermission === 'unsupported'}
                  onChange={async (e) => {
                    const enabled = !!e.value;
                    if (!enabled) {
                      setNotifyBrowser(false);
                      return;
                    }
                    const permission = await requestBrowserNotifications();
                    setBrowserPermission(permission);
                    if (permission === 'granted') {
                      setNotifyBrowser(true);
                      setNotifyError('');
                    } else {
                      setNotifyBrowser(false);
                      setNotifyError(
                        permission === 'unsupported'
                          ? 'This browser does not support desktop popups.'
                          : 'Allow notifications in the browser to use popups.'
                      );
                    }
                  }}
                />
              </label>
              <label className="notify-toggle">
                <span className="notify-toggle-copy">
                  <span className="notify-toggle-title">Sound for new notifications</span>
                  <span className="notify-toggle-hint">Play a sound when a new notification arrives</span>
                </span>
                <InputSwitch
                  checked={notifySound}
                  onChange={(e) => {
                    const enabled = !!e.value;
                    setNotifySound(enabled);
                    if (enabled) {
                      import('../utils/notificationSound').then(({ playNotificationSound }) => {
                        playNotificationSound();
                      });
                    }
                  }}
                />
              </label>
            </div>
            {notifyMessage && <div className="profile-success">{notifyMessage}</div>}
            {notifyError && <div className="profile-error">{notifyError}</div>}
            <div className="profile-form-actions">
              <Button
                type="submit"
                label={notifySaving ? 'Saving...' : 'Save notifications'}
                icon="pi pi-check"
                className="submit-btn"
                disabled={notifySaving}
              />
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ProfileSettings;
